import { createSelector, createFeatureSelector } from '@ngrx/store';
import { AppState, LookupState } from './state';
import { WebsiteInfoState, CurrentInfoState } from './state';
import { UserTypeEnum } from '../user/shared/models/enums/user-type-enum';
import { AnnouncementTypeFilterConstants } from '../shared/models/announcement-type-filter-constants';
import {
  HRRAExceptionCode,
  Locality,
  OccupationCategory,
  Specialization,
  TreeSelectModel,
} from '@hrra/common-models';
import { selectJobseekerStoreState } from './jobseeker-store/jobseeker-profile/selectors';

const selectLookups = createFeatureSelector<LookupState>('lookupMy');

export const selectLookupMy = createSelector(selectLookups, (s) => s);

export const selectCommonLookupsLoaded = createSelector(
  selectLookups,
  (s) => s.commonLookupsLoaded
);

export const selectLocalities = createSelector(
  selectLookups,
  (s) => s.localities
);

export const selectLocalitiesMap = createSelector(selectLocalities, (s) => {
  let map: { [index: string]: Locality } = {};
  s.reduce((a, c) => {
    a[c.localityId] = c;
    return a;
  }, map);
  
  return map;
});

export const selectLocalitiesMapNotEmpty = createSelector(
  selectLocalities,
  (s) => {
    if (s === null || s === undefined || s.length == 0) {
      return null;
    }
    let map: { [index: number]: Locality } = {};
    s.reduce((a, c) => {
      a[c.localityId] = c;
      return a;
    }, map);

    return map;
  }
);

export const selectCategories = createSelector(
  selectLookups,
  (s) => s.occupationCategories
);

export const selectCategoriesTree = createSelector(selectCategories, (s) => {
  let categoryTree = [];
  let categoryDictionary: { [index: number]: TreeSelectModel<number> } = {};
  let headCategories = s.filter((c) => c.isHead);
  let categories = s.filter((c) => !c.isHead);

  headCategories.forEach((headCategory) => {
    categoryDictionary[headCategory.id] = {
      key: headCategory.id.toString(),
      label: headCategory.name,
      data: headCategory.id,
      date: headCategory.id,
      children: [],
      leaf: false,
      icon: null,
      collapsedIcon: null,
      expandedIcon: null,
      parent: null,
      partialSelected: false,
      expanded:false
    };
  });

  categories.forEach((category) => {
    categoryDictionary[category.headCategoryId].children.push({
      key: category.id.toString(),
      label: category.name,
      data: category.id,
      date: category.id,
      children: null,
      leaf: true,
      icon: null,
      collapsedIcon: null,
      expandedIcon: null,
      parent: categoryDictionary[category.headCategoryId],
      partialSelected: false,
      expanded: false,
    });
  });

  for (var key in categoryDictionary) {
    var children = categoryDictionary[key].children;

    if (children != null) {
      children.sort((a, b) => a.label.localeCompare(b.label));
    }

    categoryDictionary[key].children = children;
    categoryTree.push(categoryDictionary[key]);
  }

  categoryTree.sort((a, b) => a.label.localeCompare(b.label));

  return categoryTree;
});

export const selectHeadCategories = createSelector(selectCategories, (s) =>
  s.filter((c) => c.isHead)
);

export const selectChildCategories = createSelector(selectCategories, (s) =>
  s.filter((c) => !c.isHead)
);

export const selectCategoriesMap = createSelector(selectCategories, (s) => {
  let map: { [index: number]: OccupationCategory } = {};
  s.reduce((a, c) => {
    a[c.categoryId] = c;
    return a;
  }, map);

  return map;
});

export const selectCategoriesMapNotEmpty = createSelector(
  selectCategories,
  (s) => {
    if ((s === null && s === undefined) || s.length == 0) {
      return null;
    }

    let map: { [index: number]: OccupationCategory } = {};
    s.reduce((a, c) => {
      a[c.categoryId] = c;
      return a;
    }, map);

    return map;
  }
);

export const selectAnnouncementTypes = createSelector(
  selectLookups,
  (s) => s.announcementTypes
);

export const selectAnnouncementTypeOptions = createSelector(
  selectLookups,
  (s) => s.announcementTypeOptions
);

/**
 * p-select treats an undefined model as "nothing selected" and skips the selectedItem template,
 * so the "Any" option (announcementTypeId undefined) gets key 0 to keep the placeholder text rendered
 */
export const selectAnnouncementTypeFilterOptions = createSelector(
  selectAnnouncementTypeOptions,
  (options) => options.map((o) => o.announcementTypeId === undefined ? { ...o, announcementTypeId: AnnouncementTypeFilterConstants.AnyAnnouncementTypeId } : o)
);

// Specializations

export const selectSpecializations = createSelector(
  selectLookups,
  (s) => s.specializations
);

export const selectHeadSpecializations = createSelector(selectSpecializations, (s) =>
  s.filter((c) => c.isHead)
);

export const selectChildSpecializations = createSelector(selectSpecializations, (s) =>
  s.filter((c) => !c.isHead)
);

export const selectSpecializationMap = createSelector(selectSpecializations, (s) => {
  const map: { [index: string]: Specialization } = {};
  s.reduce((a, c) => {
    a[c.specializationId] = c;
    return a;
  }, map);

  return map;
});

export const selectSpecializationsMapNotEmpty = createSelector(
  selectSpecializations,
  (s) => {
    if ((s === null && s === undefined) || s.length == 0) {
      return null;
    }

    const map: { [index: string]: Specialization } = {};
    s.reduce((a, c) => {
      a[c.specializationId] = c;
      return a;
    }, map);

    return map;
  }
);

export const selectSalaryOptions = (props: any) =>
  createSelector(selectLookups, (s) => {
    return [
      // {
      //     indexNumber: 0,
      //     name: props.emptyItemName,
      //     value: undefined,
      //     isDefault: false,
      //     isEmpty: true
      // },
      ...s.salaryFilterOptions,
    ];
  });

export const selectSalaryOptionsNew = createSelector(selectLookups, (s) => {
  return [
    // {
    //     indexNumber: 0,
    //     name: props.emptyItemName,
    //     value: undefined,
    //     isDefault: false,
    //     isEmpty: true
    // },
    ...s.salaryFilterOptions,
  ];
});

export const selectPublishDateOptions = createSelector(
  selectLookups,
  (s) => s.publishDateFilterOptions
);

export const selectSeniorityLevelOptions = createSelector(
  selectLookups,
  (s) =>  s?.seniorityLevelOptions
);

export const selectTransportTypeOptions = createSelector(
  selectLookups,
  (s) =>  s?.transportTypes
);

export const selectEmploymentFormOptions = createSelector(
  selectLookups,
  (s) =>  s?.employmentForms
);

export const selectEmploymentTypeOptions = createSelector(
  selectLookups,
  (s) =>  s?.employmentTypes
);

export const selectEmploymentNewFormsOptions = createSelector(
  selectLookups,
  (s) =>  s?.employmentFormsNew
);

export const selectWorkScheduleOptions = createSelector(
  selectLookups,
  (s) =>  s?.workSchedules
);

export const selectDrivingLicenceOptions = createSelector(
  selectLookups,
  (s) => s?.drivingLicences
);

export const selectEducationLevelOptions = createSelector(
  selectLookups,
  (s) => s?.educationLevels
);

export const selectWorldLanguages = createSelector(
  selectLookups,
  (s) => s?.worldLanguages
);

export const selectLanguageProficiencyLevels = createSelector(
  selectLookups,
  (s) => s?.languageProficiencyLevels
);

export const selectLanguagesTree = createSelector(
  selectWorldLanguages,
  selectLanguageProficiencyLevels,
  (languages, levels) => {
    const languageTree = [];
    const languageLevels = levels
    const languageDictionary: { [index: number]: TreeSelectModel<number> } = {};
    const allLanguages = languages
    let modifiedLanguageLevels = []

    allLanguages.forEach((allItems) => {
      languageDictionary[allItems.value] = {
          key: allItems.value.toString(),
          label: allItems.name,
          date: allItems.value,
          data: allItems.value,
          icon:null,
          expandedIcon:null,
          collapsedIcon:null,
          parent:null,
          leaf:null,
          partialSelected: false,
          expanded: false,
          children: []
        }
        modifiedLanguageLevels = languageLevels.map(level => ({
          key: allItems.value + '-' + (level.value).toString(),
          date: level.value,
          label: level.name,
          data: level.value,
          icon:null,
          children:null,
          expandedIcon:null,
          collapsedIcon:null,
          expanded: false,
          parent: languageDictionary[allItems.value],
          partialSelected: false,
          leaf:true,
        }))
        languageDictionary[allItems.value].children.push(...modifiedLanguageLevels)
    })

    for(const key in languageDictionary){
      const children = languageDictionary[key].children;

      if(children != null){
          children.sort((a, b) => a.label.localeCompare(b.label));
      }

      languageDictionary[key].children = children;
      languageTree.push(languageDictionary[key]);
    }

    languageTree.sort((a, b) => a.label.localeCompare(b.label));

    return languageTree;
  }
);

export const selectDefaultPublishDateOption = createSelector(
  selectLookups,
  (s) => {
    const defaultPublishDateOptions = s.publishDateFilterOptions.filter(
      (f) => f.key == 0
    );

    if (defaultPublishDateOptions.length == 0) {
      return null;
    } else if (defaultPublishDateOptions.length == 1) {
      return defaultPublishDateOptions[0];
    } else {
      throw Error('More than one default publish date filter option!');
    }
  }
);

export const selectTodayPublishDateOption = createSelector(
  selectPublishDateOptions,
  (s) => s.find((e) => e.value?.from == 0 && e.value.to == 0)
);

export const getLookupState = createFeatureSelector<WebsiteInfoState>('lookup');

export const selectPartnerSitesForFooter = createSelector(
  getLookupState,
  (state: WebsiteInfoState) => state.partnerSitesForFooter
);

export const ProviderWebsiteUrlMap = createSelector(
  getLookupState,
  (state: WebsiteInfoState) => {
    let map: { [index: number]: string } = {};
    state.partnerSitesForFooter?.reduce((a, c) => {
      a[c.id] = c.link;
      return a;
    }, map);
    return map;
  }
);

export const selectSystemConfigs = createSelector(
  getLookupState,
  (state: WebsiteInfoState) => state.configs?.systemConfigs
);

export const selectSocialMediaShareLinks = createSelector(
  selectSystemConfigs,
  (s) => s?.socialMediaShare
);

export const selectJobseekerPortalPopupEnabled = createSelector(
  selectSystemConfigs,
  (s) => s?.common?.jobseekerPortalPopupEnabled
);

export const selectBlogPortalUrl = createSelector(
  selectSystemConfigs,
  (s) => s?.common?.blogPortalUrl
);

export const selectBlogEnabled = createSelector(
  selectSystemConfigs,
  (s) => !!s?.common?.blogEnabled
);

export const selectCustomersNumberOfItemsOnPage = createSelector(
  getLookupState,
  (state: WebsiteInfoState) =>
    state.configs?.uxSettings.websiteSettings.customersNumberOfItemsOnPage
);

export const selectGeneralDefaultNumberOfListItemsOnPage = createSelector(
  getLookupState,
  (state: WebsiteInfoState) =>
    state.configs?.uxSettings.websiteSettings
      .generalDefaultNumberOfListItemsOnPage
);

export const selectFrontPageNumberOfItemsOnPage = createSelector(
  getLookupState,
  (state: WebsiteInfoState) =>
    state.configs?.uxSettings.websiteSettings.frontPageNumberOfItemsOnPage
);

export const selectSearchResultsStateInfo = createSelector(
  selectLookups,
  selectFrontPageNumberOfItemsOnPage,
  selectCommonLookupsLoaded,
  (l, p, ll) => {
    return {
      lookup: l,
      isLookupLoaded: ll,
      pageSize: p,
    };
  }
);

export const selectLocationLevelCount = createSelector(
  getLookupState,
  (state: WebsiteInfoState) =>
    state.configs?.systemConfigs.common.locationLevelCount
);

export const selectLocationRequiredLevelCount = createSelector(
  getLookupState,
  (state: WebsiteInfoState) =>
    state.configs?.systemConfigs.common.locationRequiredLevelCount
);

export const selectLocationLastHiddenLevelCount = createSelector(
  getLookupState,
  (state: WebsiteInfoState) =>
    state.configs?.systemConfigs.common.locationLastHiddenLevelCount
);

export const selectLocationFirstLevelId = createSelector(
  getLookupState,
  (s) => s.configs?.systemConfigs.common.locationFirstLevelId
);

export const selectTimeZone = createSelector(
  selectSystemConfigs,
  (s) => s?.common.timeZone
);

export const selectRecaptchaSiteKey = createSelector(
  selectSystemConfigs,
  (s) => s?.common.recaptchaSiteKey
);

export const selectPhoneCountrySettings = createSelector(
  selectSystemConfigs,
  (s) => s?.common && {
    defaultCountry: s.common.phoneDefaultCountry,
    preferredCountries: s.common.phonePreferredCountries ?? [],
  }
);

export const selectGoogleAnalyticsCode = createSelector(
  selectSystemConfigs,
  (s) => s?.common.googleAnalyticsCode
);

export const selectGoogleTagManagerCode = createSelector(
  selectSystemConfigs,
  (s) => s?.common.googleTagManagerCode
);

export const selectAnnViewCountIgnorePeriod = createSelector(
  selectSystemConfigs,
  (s) => s?.announcement.viewCountIgnorePeriod
);

export const selectIsTopGeOn = createSelector(
  selectSystemConfigs,
  (s) => s?.common.isTopGeOn
);

export const selectTopGeCode = createSelector(
  selectSystemConfigs,
  (s) => s?.common.topGeCode
);

export const selectFacebookPageId = createSelector(
  selectSystemConfigs,
  (s) => s?.common.facebookPageId
);

export const selectCurrency = createSelector(
  selectSystemConfigs,
  (s) => s?.common.currency
);

export const selectAnnouncementLocationRequiredLevelCount = createSelector(
  selectSystemConfigs,
  (s) => s?.announcement.locationRequiredLevelCount
);

//current info
export const getCurrentInfoState =
  createFeatureSelector<CurrentInfoState>('currentInfo');

export const selectCurrentDate = createSelector(
  getCurrentInfoState,
  (s) => s.currentDate
);

export const selectCurrentLanguage = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) => state.currentLanguage
);

export const selectDefaultLanguage = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) => state.defaultLanguage
);

export const selectAlternativeLanguages = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) => state.alternativeLanguages
);

// primary language first, followed by the site's alternative languages
export const selectLanguages = createSelector(
  selectDefaultLanguage,
  selectAlternativeLanguages,
  (defaultLanguage, alternativeLanguages) =>
    [defaultLanguage, ...alternativeLanguages].filter(language => language !== null && language !== undefined)
);

export const selectCurrentWebsite = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) => state.currenWbeiste
);

export const selectCurrentWebsiteDisplayName = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) =>
    state.currenWbeiste?.displayName.replace('.', '-')
);

export const selectCurrentWebsiteShortName = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) => state.currenWbeiste?.displayName.split('.')[0]
);

export const selectCurrentWebsiteNameForSeo = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) => {
    return '| ' + state.currenWbeiste?.name;
  }
);

export const selectLastListingPage = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) => state.lastListingPage
);

export const selectIsSignedIn = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) => state.isSignedIn
);

export const selectIsJobseekerSignedIn = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) =>
    state.isSignedIn && state.user?.userType == UserTypeEnum.Jobseeker
);

export const selectIsCustomerSignedIn = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) =>
    state.isSignedIn && state.user?.userType == UserTypeEnum.CustomerAgent
);

export const selectUser = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) => state.user
);

export const selectCurrentUserType = createSelector(
  getCurrentInfoState,
  (s) => s.user?.userType
);

export const selectIsPasswordChangeRequired = createSelector(
  selectUser,
  (u) => u?.isPasswordChangeRequired
);

export const selectIsError = createSelector(
  getCurrentInfoState,
  (state: CurrentInfoState) => state.isSignInError
);

export const selectFavorites = (s: AppState) => {
  return s.favorites;
};

export const selectFavoriteAnnouncementsMap = createSelector(
  selectFavorites,
  (s) => {
    return s.favoriteAnnouncementIds.reduce((a, b) => {
      a[b] = true;
      return a;
    }, {});
  }
);

export const selectFavoriteAnnouncementsCount = createSelector(
  selectFavorites,
  (s) => s.favoriteAnnouncementIds.length
);

export const selectIsSignInLoading = createSelector(
  getCurrentInfoState,
  (c) => c.loading
);

export const selectCustomerPortalUrl = createSelector(
  selectSystemConfigs,
  (s) => s?.common.customerPortalUrl
);

export const selectUserDoesNotExist = createSelector(
  getCurrentInfoState,
  (s) => {
    return (
      s.signInFailureDetails?.errorCode == HRRAExceptionCode.UserDoesNotExist
    );
  }
);

export const selectIsSignInError = createSelector(
  getCurrentInfoState,
  (s) => s.isSignInError
);
